/**
 * Hook: PATCH /api/t/{slug}/jds/{id}/status — JD lifecycle transition.
 *
 * Body: ``{ status: "draft" | "active" | "paused" | "archived" }``
 * Response: full updated `JdOut`.
 *
 * Optimistic update: writes the new status into both the cached single
 * record (`['jd', slug, id]`) and any cached list pages (`['jds', slug, …]`)
 * so the pill / dropdown re-renders before the server round-trip completes.
 * On error, rolls back from the snapshotted previous state.
 *
 * Phase 2.3 — jd-rebuild-spec.md §2.3 + Architect-lite contract.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import type { Jd, JdStatus } from "./useJd";

interface JdStatusUpdateInput {
  id: string;
  status: JdStatus;
}

interface OptimisticContext {
  prevDetail: Jd | undefined;
  prevLists: Array<readonly [readonly unknown[], Jd[] | undefined]>;
}

export function useUpdateJdStatus(): UseMutationResult<
  Jd,
  ApiError,
  JdStatusUpdateInput,
  OptimisticContext
> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const qc = useQueryClient();

  return useMutation<Jd, ApiError, JdStatusUpdateInput, OptimisticContext>({
    mutationFn: async ({ id, status }) => {
      const r = await apiClient.patch<Jd>(
        `/api/t/${tenantSlug}/jds/${id}/status`,
        { status },
      );
      return r.data;
    },
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: ["jd", tenantSlug, id] });
      await qc.cancelQueries({ queryKey: ["jds", tenantSlug] });

      const prevDetail = qc.getQueryData<Jd>(["jd", tenantSlug, id]);
      // Snapshot every cached `['jds', tenantSlug, …]` page so we can
      // restore on error regardless of which statusFilter slot it lives in.
      const prevLists = qc.getQueriesData<Jd[]>({
        queryKey: ["jds", tenantSlug],
      });

      if (prevDetail) {
        qc.setQueryData<Jd>(["jd", tenantSlug, id], {
          ...prevDetail,
          status,
        });
      }
      for (const [key, list] of prevLists) {
        if (!list) continue;
        qc.setQueryData<Jd[]>(
          key,
          list.map((j) => (j.id === id ? { ...j, status } : j)),
        );
      }

      return { prevDetail, prevLists };
    },
    onError: (_err, variables, context) => {
      if (!context) return;
      if (context.prevDetail) {
        qc.setQueryData<Jd>(
          ["jd", tenantSlug, variables.id],
          context.prevDetail,
        );
      }
      for (const [key, list] of context.prevLists) {
        qc.setQueryData(key, list);
      }
    },
    onSettled: (_data, _err, variables) => {
      void qc.invalidateQueries({ queryKey: ["jd", tenantSlug, variables.id] });
      void qc.invalidateQueries({ queryKey: ["jds", tenantSlug] });
    },
  });
}
